import { calculateSaleQuantity, OrderSaleQuantityInput } from './orderProductSaleQuantity';

export interface OrderProductLineDraft extends OrderSaleQuantityInput {
  productVariantId?: string | null;
  name?: string;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  thickness?: number | null;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  netWeight?: number | null;
  grossWeight?: number | null;
  cnCode?: string;
  countryOfOrigin?: string;
  description?: string;
  unitPrice?: number;
  totalPrice?: number;
  totalValue?: number;
  currency?: string;
}

export const getLineCurrency = (
  product: OrderProductLineDraft | undefined,
  fallbackCurrency: string
) => product?.currency || fallbackCurrency || 'PLN';

export const getLineTotal = (product: OrderProductLineDraft | undefined) => {
  const quantity = calculateSaleQuantity(product) ?? 0;
  const unitPrice = Number(product?.unitPrice || 0);
  return Number.isFinite(quantity) && Number.isFinite(unitPrice) ? quantity * unitPrice : 0;
};

export const getTotalsByCurrency = (
  products: readonly OrderProductLineDraft[] = [],
  fallbackCurrency = 'PLN'
) =>
  products.reduce<Record<string, number>>((totals, product) => {
    const name = String(product?.name || '').trim();
    const total = getLineTotal(product);
    const currency = getLineCurrency(product, fallbackCurrency);

    if (!name && total === 0) {
      return totals;
    }

    totals[currency] = (totals[currency] || 0) + total;
    return totals;
  }, {});

export const getOrderBaseCurrency = (
  products: readonly OrderProductLineDraft[] = [],
  preferredCurrency = 'PLN'
) => {
  const currencies = Array.from(
    new Set(
      products
        .map((product) => product.currency)
        .filter((currency): currency is string => Boolean(currency))
    )
  );

  return currencies.length === 1 ? currencies[0] : preferredCurrency;
};

export const normalizeCreateOrderProducts = (
  products: readonly OrderProductLineDraft[] = [],
  fallbackCurrency = 'PLN',
  includeSalePriceFields = true,
) =>
  products.map((product) => {
    const {
      productCategoryName,
      productCategoryColor,
      productCategoryDimensionDisplayProfile,
      ...persistableProduct
    } = product;

    if (!includeSalePriceFields) {
      const {
        currency,
        saleQuantity,
        saleUnit,
        totalPrice,
        totalValue,
        unitPrice,
        ...operationalProduct
      } = persistableProduct as OrderProductLineDraft & { saleQuantity?: number };

      return operationalProduct;
    }

    return {
      ...persistableProduct,
      currency: product.currency || fallbackCurrency,
    };
  });
